'use client';

/**
 * ComposerProjectCard — project picker (existing or new).
 *
 * Reads `useCartStore.{projectId, projectName, isNewProject}` and
 * the live project list from `useProjects`. Typeahead with a
 * "Create new" affordance for unmatched search terms; once a
 * project is selected, the input becomes a chip with an X to
 * clear.
 *
 * Parallel implementation of the legacy drawer's project section
 * (`cart-drawer.tsx`) — reads the same hooks and store, emits
 * with RailCard chrome.
 *
 * @see klyde_edge/composition/cards/composer.py — composer_project spec
 *
 * @layer presentation
 * @arch_component_type card
 * @arch_domain procurement
 */

import { useEffect, useMemo, useState } from 'react';
import { FolderOpen, Plus, X } from 'lucide-react';
import { useCartStore } from '@/lib/stores/cart-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useProjects } from '@/lib/api/hooks';
import { text } from '@/lib/design/tokens';
import { useCardExport } from '@/lib/design/card-export';
import { useAffordance } from '@/lib/stores/total-matrix-store';
import { cn } from '@/lib/utils/cn';
import { RailCard } from '@/components/copilot/rail-insights/rail-card';

export function ComposerProjectCard() {  const projectId = useCartStore((s) => s.projectId);
  const projectName = useCartStore((s) => s.projectName);
  const isNewProject = useCartStore((s) => s.isNewProject);
  const setProject = useCartStore((s) => s.setProject);
  const setNewProject = useCartStore((s) => s.setNewProject);

  // Scene-only export — the chosen/typed project (cart-store draft).
  useCardExport(useMemo(() => ({
    label: 'Project',
    content_key: 'composer_project',
    surface: 'composer' as const,
    status: 'loaded' as const,
    query_key: null,
    text: `Project: ${projectName || '—'}${isNewProject ? ' (new)' : ''}`,
    json: {
      project_id: projectId ?? null,
      project_name: projectName || null,
      is_new: isNewProject,
    },
  }), [projectId, projectName, isNewProject]));

  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const searchTestId = useAffordance('composer.project.search');

  const projectsStrategy = useCapabilityStore((s) => s.strategyName('obeya', 'list'));
  const { data } = useProjects(projectsStrategy ?? '', { limit: 50 });

  const filtered = useMemo(() => {
    const projects = data?.projects ?? [];
    if (!search) return projects.slice(0, 10);
    const s = search.toLowerCase();
    return projects.filter((p) => p.name.toLowerCase().includes(s)).slice(0, 10);
  }, [data, search]);

  useEffect(() => {
    const handler = () => setShowDropdown(false);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  return (
    <RailCard title="Project" icon={FolderOpen}>
      {projectName ? (
        <div className="flex items-center justify-between rounded-md border border-border bg-background px-2.5 py-1.5">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className={cn(text.title, 'truncate')}>{projectName}</span>
            {isNewProject && (
              <span className={cn(text.muted, 'rounded bg-primary/20 px-1.5 py-0.5 text-primary uppercase tracking-wider shrink-0')}>
                New
              </span>
            )}
          </div>
          <button
            onClick={() => setProject(null, null)}
            className="text-muted-foreground hover:text-destructive transition-colors p-0.5 -m-0.5"
            title="Clear project"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <div className="relative" onClick={(e) => e.stopPropagation()}>
          <input
            data-testid={searchTestId}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
            placeholder="Search or create new…"
            className={cn(
              text.body,
              'w-full h-8 px-2.5 rounded-md border border-border bg-background',
              'placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary',
            )}
          />
          {showDropdown && (
            <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-lg">
              <div className="max-h-40 overflow-auto p-1">
                {filtered.map((p) => (
                  <ProjectResultRow
                    key={p.semantic_id}
                    id={p.semantic_id}
                    name={p.name}
                    onSelect={() => {
                      setProject(p.semantic_id, p.name);
                      setSearch('');
                      setShowDropdown(false);
                    }}
                  />
                ))}
                {search && (
                  <button
                    onClick={() => {
                      setNewProject(search);
                      setSearch('');
                      setShowDropdown(false);
                    }}
                    className={cn(
                      text.body,
                      'w-full rounded px-2 py-1.5 text-left text-primary hover:bg-accent border-t border-border mt-1 pt-2 flex items-center gap-1.5',
                    )}
                  >
                    <Plus className="h-3 w-3" />
                    Create &quot;{search}&quot;
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </RailCard>
  );
}

/** One real project row in the search dropdown — extracted so
 *  `useAffordance` (per-project dynamic address) can be called
 *  cleanly once per rendered result, same pattern as
 *  `IntentScheduleLineRow` / `ComposerLineRow`. */
function ProjectResultRow({
  id,
  name,
  onSelect,
}: {
  id: string;
  name: string;
  onSelect: () => void;
}) {
  const selectTestId = useAffordance('composer.project.select_result', { id });
  return (
    <button
      data-testid={selectTestId}
      onClick={onSelect}
      className={cn(text.body, 'w-full rounded px-2 py-1.5 text-left hover:bg-accent')}
    >
      {name}
    </button>
  );
}
